import { getPlatformServerUrl, getViewMode, ViewMode } from 'src/utils/env'

import { Translation } from './Language'
import { TranscriptionDocumentMetadata } from './TranscriptionDocumentMetadata'

export type TranscriptType = 'asr_only' | 'edit' | 'immediate' | 'rough_draft'

export type CustomerType = 'legal_assistant' | 'court_reporter' | 'media' | 'education' | 'general_capture' | 'other'

export type MediaInputType = 'rtmp_push' | 'rtmp_pull' | 'websocket' | 'web_url' | 'onsite' | 'dial_in' | 'decoder' | 'icap'

export type RecordingStatus = 'live' | 'stopped'

export type OrderStatus = { name: string; message: string | null }
export type Order = {
    id: string
    name: string
    status: OrderStatus
}

export type TranscriptMetadata = {
    transcriptType: string
    sessionState?: {
        startTime?: Date
        deposition?: SessionDeposition
        recording?: {
            status: RecordingStatus | null
            stopReason: string | null
            startedAt: string | null
        }
        recordings?: Array<{ mediaDuration: number }>
    }
    ablyTokenUrl: string
    ablyClientOptions: Record<string, any>
    sessionId?: number
    scheduledEventId: number
    downloadable: boolean
    transcriptionJobId: number
    sessionName?: string
    sessionStartAt?: string // booked/scheduled start
    sessionEndAt?: string // booked/scheduled end
    sessionStartedAt?: string // actual start
    sessionFinishedAt?: string // actual
    customerId: number
    customerName: string
    customerType: CustomerType | null
    suppressSpeakerName: boolean
    generativeAiEnabled: boolean
    vertical: string
    originalLanguageCode: string
    translations: Translation[]
    orderId: string
    order: Order
    inputType: MediaInputType
    viewMode: ViewMode | null
    fileUrl?: string
}

export enum SessionDeposition {
    NEW = 'new',
    SCHEDULED = 'scheduled',
    LIVE = 'live',
    FINISHED = 'finished',
    CANCELED = 'canceled',
    DATA_DELETION_REQUESTED = 'data_deletion_requested',
    DATA_DELETED = 'data_deleted',
}

export type TranscriptMetadataJSON = {
    transcript_type: TranscriptType
    session_start_time: string
    session_state?: {
        deposition: { status: SessionDeposition }
        recording?: { startedAt: string | null; status: string | null; stopReason: string | null }
        recordings?: Array<{ mediaDuration: number }>
    }
    ably_token_url: string
    ably_client_options: Record<string, any>
    trax_session_id?: number
    scheduled_event_id: number
    DL: boolean
    transcription_job_id: number
    session_name: string
    session_start_at: string // booked/scheduled start
    session_end_at: string // booked/scheduled end
    session_started_at: string // actual start
    session_finished_at: string // actual end
    customer_id: number
    customer_name: string
    suppress_speaker_names: boolean
    generative_ai_enabled: boolean
    vertical: string
    language_code: string
    languages?: { code: string; name: string }[]
    order_id: string
    order: {
        id: string
        name: string
        status: { name: string; message: string | null }
    }
    customer?: {
        type: string | null
    }
    input_type: string
}

export const toTranscriptMetadata = (json: TranscriptMetadataJSON): TranscriptMetadata => ({
    transcriptType: json.transcript_type,
    sessionState: {
        startTime: new Date(json.session_start_time),
        deposition: json.session_state?.deposition?.status,
        recording: {
            startedAt: json.session_state?.recording?.startedAt || null,
            status: toRecordingStatus(json.session_state?.recording?.status),
            stopReason: json.session_state?.recording?.stopReason || null,
        },
        recordings: json.session_state?.recordings || [],
    },
    ablyTokenUrl: formatAblyTokenUrl(json.ably_token_url),
    ablyClientOptions: json.ably_client_options,
    sessionId: json.trax_session_id,
    scheduledEventId: json.scheduled_event_id,
    downloadable: json.DL,
    transcriptionJobId: json.transcription_job_id,
    sessionStartAt: json.session_start_at,
    sessionEndAt: json.session_end_at,
    sessionStartedAt: json.session_started_at,
    sessionFinishedAt: json.session_finished_at,
    sessionName: json.session_name,
    customerId: json.customer_id,
    customerName: json.customer_name,
    customerType: (json.customer?.type as CustomerType) || null,
    suppressSpeakerName: json.suppress_speaker_names,
    generativeAiEnabled: json.generative_ai_enabled,
    vertical: json.vertical,
    originalLanguageCode: json.language_code,
    translations: json.languages || [],
    orderId: json.order_id,
    order: json.order,
    inputType: json.input_type as MediaInputType,
    viewMode: getViewMode(),
})

// Platform's wire payload types status as a free-form string; narrow it to the known
// domain values at the boundary and drop anything unrecognized to null.
const toRecordingStatus = (status: string | null | undefined): RecordingStatus | null =>
    status === 'live' || status === 'stopped' ? status : null

const formatAblyTokenUrl = (urlString: string) => {
    try {
        const { pathname, search } = new URL(urlString)

        return `${getPlatformServerUrl()}${pathname}${search}`
    } catch (e) {
        return ''
    }
}

export const toTranscriptMetadataFromDocument = (document: TranscriptionDocumentMetadata): TranscriptMetadata => {
    const uploadedAtISO = document.uploadedAt.toISOString()
    const isCompleted = document.transcriptState === 'completed'

    return {
        transcriptType: '',
        // while documents do not have an actual "session", we need to establish
        // that recording status is null, otherwise the app will attempt
        // to download the media
        sessionState: {
            startTime: document.uploadedAt,
            recording: {
                status: null,
                stopReason: null,
                startedAt: null,
            },
        },
        ablyTokenUrl: '',
        ablyClientOptions: {},
        scheduledEventId: NaN,
        downloadable: false,
        transcriptionJobId: 0,
        sessionName: document.documentTitle,
        sessionStartAt: uploadedAtISO,
        sessionEndAt: '',
        sessionFinishedAt: isCompleted ? uploadedAtISO : '',
        customerId: document.customerId,
        customerName: document.customerName,
        customerType: null,
        suppressSpeakerName: false,
        generativeAiEnabled: Boolean(document.generativeAiEnabled),
        vertical: '',
        originalLanguageCode: '',
        translations: [],
        orderId: '',
        order: {
            id: '',
            name: '',
            status: {
                name: '',
                message: null,
            },
        },
        inputType: 'web_url',
        viewMode: 'document',
        fileUrl: document.fileUrl,
    }
}

export type SessionPhase = 'before_session_started' | 'during_session' | 'after_session_ended'

export const resolveSessionPhase = (
    metadata: Pick<TranscriptMetadata, 'sessionState' | 'sessionStartedAt' | 'sessionFinishedAt'>,
): SessionPhase => {
    const { sessionState, sessionStartedAt, sessionFinishedAt } = metadata

    const isSessionEnded =
        !sessionState?.deposition ||
        [SessionDeposition.FINISHED, SessionDeposition.CANCELED].includes(sessionState.deposition) ||
        (sessionFinishedAt && new Date() >= new Date(sessionFinishedAt))
    if (isSessionEnded) return 'after_session_ended'

    const isSessionStarted = sessionStartedAt && new Date() >= new Date(sessionStartedAt)
    if (isSessionStarted) return 'during_session'

    return 'before_session_started'
}

export const isJobLive = (metadata: TranscriptMetadata) => !isNaN(metadata.scheduledEventId)

export const isJobFinished = (metadata: TranscriptMetadata) => {
    if (metadata.sessionFinishedAt) return true

    // A live recording overrides the booked-end heuristic below: Platform derives
    // sessionStartedAt from the recording's mediaStartedAt, which Pylon never sends, so a
    // live session in overtime would otherwise be wrongly reported as finished (LG-2844).
    if (metadata.sessionState?.recording?.status === 'live') return false

    if (!metadata.sessionStartedAt && metadata.sessionEndAt) {
        return new Date() > new Date(metadata.sessionEndAt)
    }

    return false
}

export const isRecordingStarted = (metadata: TranscriptMetadata) => {
    const recordings = metadata.sessionState?.recordings

    return !!recordings && recordings.length > 0 && recordings.some((r) => r.mediaDuration > 0)
}

export const isRecordingStopped = (metadata: TranscriptMetadata) => !!metadata && metadata.sessionState?.recording?.status === 'stopped'

export const isLanguageSelectionAvailable = (metadata: TranscriptMetadata | null) => {
    const translationsAmount = metadata?.translations?.length

    return typeof translationsAmount === 'number' ? translationsAmount > 1 : false
}

// in case if session name is missing, use order name as a fallback.
// a whitespace-only name counts as missing: it would otherwise render a blank heading and a
// filename with stray spaces.
export const getSessionTitle = (metadata: TranscriptMetadata | null) => metadata?.sessionName?.trim() || metadata?.order?.name?.trim() || ''
